import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  notification,
  Select,
  Space,
  Spin,
} from 'antd'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Can, Tags } from '../../components'
import { ModifiedModalFooter } from '../../elements'
import { handleBackendError } from '../../utils'
import { getUserFilteredList } from '../users/api'

import { createScan } from './api'

let tempScanErrors = []
let tempScanSuccess = []

const ScanModal = ({
  countries,
  errorMessage,
  hideModal,
  isModalLoading,
  onSuccess,
  setModalLoading,
}) => {
  const [form] = Form.useForm()
  const [scanErrors, setScanErrors] = useState([])
  const [filteredUserList, setFilteredUsers] = useState([])
  const [fetching, setFetching] = useState(false)

  const onUserSearch = (searchValue) => {
    if (!form.getFieldValue('country_id') || searchValue.length < 3)
      return null

    const selectedCountryId = form.getFieldValue('country_id')

    const searchParam = `id_or_name_or_email=${searchValue}&country_id=${selectedCountryId}`
    setFetching(true)

    getUserFilteredList(searchParam)
      .then(({ data }) => {
        const result = data.data.map(user => ({
          label: `${user.name} (${user.email})`,
          value: user.id,
        }))

        setFilteredUsers(result)
        setFetching(false)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Fetching user list failed!'),
          title: 'Error',
        })

        setFetching(false)
      })
  }

  const setTags = (tags) => {
    if (tags.length > 0)
      form.setFieldValue('qr_code', tags)
  }

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      const qrCodeTags = form.getFieldValue('qr_code')
      if (qrCodeTags?.length === 0 || !qrCodeTags) {
        notification.error({
          description: 'Please provide valid QR code(s)!',
          title: 'Form is invalid!',
        })

        return
      }

      setScanErrors([])

      form
        .validateFields()
        .then(() => {
          setModalLoading(true)

          const user_id = form.getFieldValue('user_id')

          const dataObject = {
            ...form.getFieldsValue(['comment']),
            user_id,
          }

          sendScanToBackend(dataObject, qrCodeTags)
        }).catch((error) => {
          notification.error({
            description: handleBackendError(error, 'Form is invalid!'),
            title: 'Error',
          })
        })
    } else {
      hideModal()
    }
  }

  const sendScanToBackend = (data, qrCodeTags, index = 0) => {
    const nextIndex = index + 1

    createScan({
      ...data,
      qr_code: qrCodeTags[index],
    })
      .then(() => {
        tempScanSuccess.push(qrCodeTags[index])
      })
      .catch((error) => {
        if (error?.response?.data?.details?.qr_code) {
          tempScanErrors.push(qrCodeTags[index])
        }
      })
      .finally(() => {
        if (qrCodeTags[nextIndex]) {
          sendScanToBackend(data, qrCodeTags, nextIndex)
        } else {
          if (tempScanSuccess.length > 0) {
            onSuccess({ description: `Successfully created ${tempScanSuccess.length} QR code(s)`, duration: 6 }, !(tempScanErrors.length === 0))
          }

          if (tempScanErrors.length > 0) {
            notification.error({
              description: `${tempScanErrors.length} QR code(s) create failed`,
              title: 'Error',
            })

            setScanErrors(tempScanErrors)
          }

          tempScanErrors = []
          tempScanSuccess = []

          setModalLoading(false)
        }
      })
  }

  const hasCountryId = Form.useWatch('country_id', form)

  const countryOptions = countries.map(({ id, text }) => ({ label: text, value: id }))
  const debouncedSearch = debounce(onUserSearch, 1000)

  return (
    <Modal
      footer={[
        <ModifiedModalFooter key="padding">
          <Space>
            <Button key="back" name="cancelScanModalBtnSelenium" onClick={hideModal}>
              Cancel
            </Button>
            <Can
              name="exportScansSelenium"
              requiredPermission="scan.post.create"
              yes={() => (
                <Button data-testid="submitScanModalSelenium" key="submit" loading={isModalLoading} name="submitScanModalSelenium" onClick={handleSubmit} type="primary">
                  Ok
                </Button>
              )}
            />
          </Space>
        </ModifiedModalFooter>,
      ]}
      onCancel={hideModal}
      open
      title="New Admin Scan"
    >
      <Form
        data-test="scan-details-form"
        data-testid="scanForm"
        form={form}
        layout="vertical"
      >
        <Form.Item label="Country" name="country_id" rules={[{ message: 'Please select a country!', required: true }]}>
          <Select data-testid="country_id" options={countryOptions} />
        </Form.Item>

        <Form.Item label="User name or email" name="user_id" rules={[{ message: 'Please select a user!', required: true }]}>
          <Select
            data-testid="user_id"
            disabled={!hasCountryId}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={filteredUserList}
            showSearch={{filterOption: false, onSearch: debouncedSearch}}
          />
        </Form.Item>

        <Tags errors={scanErrors} onChange={setTags} />

        <Form.Item
          label="Comment"
          name="comment"
          rules={[{ message: 'Please provide your comment!', required: true }]}
        >
          <Input.TextArea />
        </Form.Item>
      </Form>

      {errorMessage && (
        <Alert
          description={errorMessage}
          showIcon
          style={{ marginTop: 20 }}
          title="Error"
          type="error"
        />
      )}
    </Modal>
  )
}

ScanModal.propTypes = {
  countries: PropTypes.array.isRequired,
  errorMessage: PropTypes.string,
  hideModal: PropTypes.func.isRequired,
  isModalLoading: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired,
  setModalLoading: PropTypes.func.isRequired,
}

export default ScanModal
